import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PeopleIcon from "@mui/icons-material/People";
import SchoolIcon from "@mui/icons-material/School";
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import api from "../../api";
import { toast } from "react-toastify";
import AnalyticsPageShell, { CARD_SX } from "./AnalyticsPageShell";
import AnalyticsFiltersBar from "./AnalyticsFiltersBar";
import useAnalyticsFilters from "./useAnalyticsFilters";
import AnalyticsDataTable from "./AnalyticsDataTable";
import SchoolExamProfile from "./SchoolExamProfile";

const SELECTION_TYPE_LABELS = {
  super_selective: "Super-selective (pure merit)",
  consortium: "Consortium (two-stage)",
  all_in_one: "All-in-one (single day)",
  school_specific: "School-specific",
  other: "Other",
};

const SELECTION_TYPE_OPTIONS = Object.entries(SELECTION_TYPE_LABELS).map(([value, label]) => ({ value, label }));

function selectionTypeLabel(value) {
  if (!value) return "";
  return SELECTION_TYPE_LABELS[value] || value;
}

const TILE_CONFIGS = [
  { key: "areas_covered", label: "Areas Covered", Icon: LocationOnIcon, iconBg: "#ede9fe", iconColor: "#7c3aed" },
  { key: "total_students", label: "Total Students", Icon: PeopleIcon, iconBg: "#dcfce7", iconColor: "#16a34a" },
  { key: "grammar_schools", label: "Grammar Schools", Icon: SchoolIcon, iconBg: "#dbeafe", iconColor: "#2563eb" },
  { key: "independent_schools", label: "Independent Schools", Icon: SchoolIcon, iconBg: "#dcfce7", iconColor: "#16a34a" },
  { key: "schools_mapped", label: "Schools on Map", Icon: SchoolIcon, iconBg: "#e0f2fe", iconColor: "#0284c7" },
];

function KpiTile({ label, value, Icon, iconBg, iconColor }) {
  return (
    <Paper sx={{ ...CARD_SX, display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ width: 52, height: 52, borderRadius: "50%", bgcolor: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon sx={{ color: iconColor, fontSize: 26 }} />
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{(value ?? 0).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography>
      </Box>
    </Paper>
  );
}

function loadGoogleMaps(apiKey) {
  if (!apiKey) {
    return Promise.reject(new Error("Missing Google Maps API key"));
  }
  if (window.google?.maps) {
    return Promise.resolve(window.google.maps);
  }
  const existing = document.querySelector("script[data-geo-maps='1']");
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(window.google.maps));
      existing.addEventListener("error", () => reject(new Error("Failed to load Google Maps")));
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
    script.async = true;
    script.defer = true;
    script.dataset.geoMaps = "1";
    script.onload = () => resolve(window.google.maps);
    script.onerror = () => reject(new Error("Failed to load Google Maps"));
    document.head.appendChild(script);
  });
}

function itemMatchesSubject(item, subjectId) {
  if (!subjectId) return true;
  const want = Number(subjectId);
  if ((item.subject_ids || []).some((id) => Number(id) === want)) return true;
  return (item.subjects || []).some((s) => Number(s.id) === want);
}

function itemSubjectLabel(item) {
  const names = (item.subjects || []).map((s) => s.name).filter(Boolean);
  return names.length ? names.join(", ") : "Unassigned";
}

function categoryColor(category) {
  const name = String(category || "").toLowerCase();
  if (name === "grammar" || name === "grammer") return "#2563eb";
  if (name === "independent") return "#16a34a";
  return "#64748b";
}

function GeographicMap({ mapConfig, rows, schools, origin }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [mapError, setMapError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const apiKey = mapConfig?.google_maps_api_key;
    if (!apiKey) {
      setMapError("Configure GOOGLE_MAPS_API_KEY (or GOOGLE_API_KEY) to show the map.");
      return undefined;
    }

    loadGoogleMaps(apiKey)
      .then((maps) => {
        if (cancelled || !mapRef.current) return;

        markersRef.current.forEach((marker) => marker.setMap(null));
        markersRef.current = [];

        const center = {
          lat: Number(mapConfig?.default_center?.lat ?? 54.5),
          lng: Number(mapConfig?.default_center?.lng ?? -2.5),
        };
        const zoom = Number(mapConfig?.default_zoom ?? 6);
        const ukBoundsConfig = mapConfig?.bounds || {
          south: 49.8,
          west: -8.6,
          north: 58.9,
          east: 2.0,
        };
        const ukBounds = new maps.LatLngBounds(
          { lat: Number(ukBoundsConfig.south), lng: Number(ukBoundsConfig.west) },
          { lat: Number(ukBoundsConfig.north), lng: Number(ukBoundsConfig.east) }
        );

        if (!mapInstanceRef.current) {
          mapInstanceRef.current = new maps.Map(mapRef.current, {
            center,
            zoom,
            minZoom: 5,
            maxZoom: 14,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
            restriction: {
              latLngBounds: ukBounds,
              strictBounds: false,
            },
          });
        }

        const map = mapInstanceRef.current;
        const infoWindow = new maps.InfoWindow();
        const schoolBounds = new maps.LatLngBounds();
        let hasSchoolPoints = false;

        const isInUk = (lat, lng) =>
          lat >= Number(ukBoundsConfig.south) - 0.5 &&
          lat <= Number(ukBoundsConfig.north) + 0.5 &&
          lng >= Number(ukBoundsConfig.west) - 0.5 &&
          lng <= Number(ukBoundsConfig.east) + 0.5;

        (rows || []).forEach((row) => {
          if (row.latitude == null || row.longitude == null) return;
          const position = { lat: Number(row.latitude), lng: Number(row.longitude) };
          if (!isInUk(position.lat, position.lng)) return;
          const marker = new maps.Marker({
            map,
            position,
            title: row.location,
            zIndex: 2,
            icon: {
              path: maps.SymbolPath.CIRCLE,
              scale: 9,
              fillColor: "#7c3aed",
              fillOpacity: 0.95,
              strokeColor: "#ffffff",
              strokeWeight: 2,
            },
          });
          marker.addListener("click", () => {
            infoWindow.setContent(
              `<div style="min-width:160px">
                <strong>${row.location}</strong><br/>
                Students: ${row.student_count ?? 0}<br/>
                Teachers: ${row.teacher_count ?? 0}<br/>
                Groups: ${row.batches_running ?? 0}
              </div>`
            );
            infoWindow.open({ map, anchor: marker });
          });
          markersRef.current.push(marker);
        });

        (schools || []).forEach((school) => {
          if (school.latitude == null || school.longitude == null) return;
          const position = { lat: Number(school.latitude), lng: Number(school.longitude) };
          if (!isInUk(position.lat, position.lng)) return;
          const color = categoryColor(school.category);
          const marker = new maps.Marker({
            map,
            position,
            title: school.name,
            zIndex: 1,
            icon: {
              path: maps.SymbolPath.CIRCLE,
              scale: 5,
              fillColor: color,
              fillOpacity: 0.95,
              strokeColor: "#ffffff",
              strokeWeight: 1,
            },
          });
          marker.addListener("click", () => {
            const papersHref = `/admin/papers?school_id=${school.id}`;
            const mocksHref = `/admin/mock-exams?school_id=${school.id}`;
            infoWindow.setContent(
              `<div style="min-width:200px">
                <strong>${String(school.name || "").replace(/</g, "&lt;")}</strong><br/>
                Nation: ${school.nation || "—"}<br/>
                Region: ${school.region || "—"}<br/>
                Category: ${school.category || "—"}<br/>
                Selection: ${selectionTypeLabel(school.selection_type) || "—"}<br/>
                Consortium: ${String(school.consortium_name || "—").replace(/</g, "&lt;")}<br/>
                Exam: ${String(school.profile?.format_line || school.exam_board_name || "—").replace(/</g, "&lt;")}<br/>
                Papers: ${school.paper_count ?? 0} own / ${school.shared_paper_count ?? 0} shared<br/>
                Practice: ${school.practice_paper_count ?? 0} own / ${school.shared_practice_paper_count ?? 0} shared<br/>
                Mocks: ${school.mock_exam_count ?? 0} own / ${school.shared_mock_exam_count ?? 0} shared<br/>
                <a href="${papersHref}">Open papers</a> · <a href="${mocksHref}">Open mock exams</a>
              </div>`
            );
            infoWindow.open({ map, anchor: marker });
          });
          markersRef.current.push(marker);
          schoolBounds.extend(position);
          hasSchoolPoints = true;
        });

        if ((mapConfig?.focus_region || origin) && hasSchoolPoints) {
          map.fitBounds(schoolBounds, { top: 40, right: 40, bottom: 40, left: 40 });
          maps.event.addListenerOnce(map, "bounds_changed", () => {
            if (map.getZoom() > 10) map.setZoom(10);
            if (map.getZoom() < 7) map.setZoom(7);
          });
        } else if (mapConfig?.focus_region) {
          map.setCenter(center);
          map.setZoom(zoom);
        } else {
          map.fitBounds(ukBounds, { top: 24, right: 24, bottom: 24, left: 24 });
          maps.event.addListenerOnce(map, "bounds_changed", () => {
            if (map.getZoom() > 6) map.setZoom(6);
          });
        }

        setMapError(null);
      })
      .catch((err) => {
        if (!cancelled) setMapError(err.message || "Failed to load Google Maps");
      });

    return () => {
      cancelled = true;
    };
  }, [mapConfig, rows, schools, origin]);

  if (mapError) {
    return <Alert severity="warning" sx={{ mb: 2 }}>{mapError}</Alert>;
  }

  return (
    <Paper sx={{ ...CARD_SX, mb: 2, p: 0, overflow: "hidden" }}>
      <Box sx={{ px: 2, pt: 2, pb: 1, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Schools &amp; Areas Map</Typography>
        <Typography variant="caption" color="text.secondary">
          Purple = teaching areas · Blue = Grammar · Green = Independent · Grey = other
        </Typography>
      </Box>
      <Box ref={mapRef} sx={{ width: "100%", height: { xs: 420, md: 560 } }} />
    </Paper>
  );
}

export default function GeographicAnalytics() {
  const [courseId, setCourseId] = useState("");
  const [academicCourseId, setAcademicCourseId] = useState("");
  const [mode, setMode] = useState("");
  const [regionId, setRegionId] = useState("");
  const [nation, setNation] = useState("");
  const [category, setCategory] = useState("");
  const [selectionType, setSelectionType] = useState("");
  const [consortiumName, setConsortiumName] = useState("");
  const [consortiumOptions, setConsortiumOptions] = useState([]);
  const [subjectId, setSubjectId] = useState("");
  const [schoolQuery, setSchoolQuery] = useState("");
  // Coverage gaps (same counts as the admin assistant's school_paper_gaps).
  const [missingPapers, setMissingPapers] = useState(false);
  const [noMocks, setNoMocks] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [openNations, setOpenNations] = useState({ England: false });
  const [summary, setSummary] = useState({});
  const [rows, setRows] = useState([]);
  const [schools, setSchools] = useState([]);
  const [regions, setRegions] = useState([]);
  const [regionSummary, setRegionSummary] = useState([]);
  const [mapConfig, setMapConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  // v2 (21 Oct 2026): exam board, gender, distance from a postcode, data gaps.
  const [examBoard, setExamBoard] = useState("");
  const [gender, setGender] = useState("");
  const [postcodeInput, setPostcodeInput] = useState("");
  const [postcode, setPostcode] = useState("");
  const [radius, setRadius] = useState("10");
  const [examBoardOptions, setExamBoardOptions] = useState([]);
  const [dataGaps, setDataGaps] = useState({});
  const [origin, setOrigin] = useState(null);
  const [gapKey, setGapKey] = useState("");

  const { courses, academicCourses } = useAnalyticsFilters({ courseId, academicCourseId });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { include_schools: 1 };
      if (courseId) params.course_id = courseId;
      if (academicCourseId) params.academic_course_id = academicCourseId;
      if (mode) params.mode = mode;
      if (regionId) params.region_id = regionId;
      if (nation && !regionId) params.nation = nation;
      if (category) params.category = category;
      if (selectionType) params.selection_type = selectionType;
      if (consortiumName) params.consortium_name = consortiumName;
      if (examBoard) params.exam_board = examBoard;
      if (gender) params.gender = gender;
      if (postcode) {
        params.postcode = postcode;
        params.radius_miles = radius;
      }
      const res = await api.get("admin/analytics/geographic", { params });
      const d = res.data?.data || {};
      setSummary(d.summary || {});
      setRows(d.rows || []);
      const nextSchools = d.schools || [];
      setSchools(nextSchools);
      setSelectedSchool((prev) => {
        if (!prev) return null;
        return nextSchools.find((s) => String(s.id) === String(prev.id)) || null;
      });
      setRegions(d.regions || []);
      setRegionSummary(d.region_summary || []);
      setMapConfig(d.map || null);
      setConsortiumOptions(d.filter_options?.consortiums || []);
      setExamBoardOptions(d.filter_options?.exam_boards || []);
      setDataGaps(d.data_gaps || {});
      setOrigin(d.origin || null);
      if (postcode && !d.origin) toast.warn(`Postcode "${postcode}" was not found.`);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load geographic data");
      setSummary({});
      setRows([]);
      setSchools([]);
      setRegions([]);
      setRegionSummary([]);
      setMapConfig(null);
      setConsortiumOptions([]);
    } finally {
      setLoading(false);
    }
  }, [courseId, academicCourseId, mode, regionId, nation, category, selectionType, consortiumName, examBoard, gender, postcode, radius]);

  useEffect(() => { load(); }, [load]);

  const subjectOptions = useMemo(() => {
    const map = new Map();
    (schools || []).forEach((school) => {
      [...(school.papers || []), ...(school.mock_exams || [])].forEach((item) => {
        (item.subjects || []).forEach((s) => {
          if (s?.id && !map.has(Number(s.id))) {
            map.set(Number(s.id), { id: Number(s.id), name: s.name || `Subject ${s.id}` });
          }
        });
      });
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [schools]);

  const areaColumns = [
    { key: "location", label: "Area" },
    { key: "student_count", label: "Students" },
    { key: "teacher_count", label: "Teachers" },
    { key: "batches_running", label: "Groups Running" },
  ];

  const schoolColumns = [
    { key: "name", label: "School", render: (row) => (
      <Button
        variant="text"
        size="small"
        onClick={() => {
          setSelectedSchool(row);
          setSchoolQuery(row.name || "");
        }}
        sx={{ textTransform: "none", fontWeight: 700, px: 0, justifyContent: "flex-start" }}
      >
        {row.name}
      </Button>
    ) },
    { key: "nation", label: "Nation" },
    { key: "region", label: "Region" },
    { key: "category", label: "Category" },
    { key: "selection_type", label: "Selection type", render: (row) => selectionTypeLabel(row.selection_type) || "—" },
    { key: "consortium_name", label: "Consortium", render: (row) => row.consortium_name || "—" },
    { key: "exam_board", label: "Exam board", render: (row) => row.exam_board_name || row.exam_board || "—" },
    {
      key: "gender",
      label: "Gender",
      render: (row) =>
        row.gender ? row.gender.charAt(0).toUpperCase() + row.gender.slice(1) : <Box component="span" sx={{ color: "#B4690E" }}>—</Box>,
    },
    ...(origin ? [{ key: "distance_miles", label: "Distance", render: (row) => `${row.distance_miles} mi` }] : []),
    { key: "paper_count", label: "Own past" },
    { key: "shared_paper_count", label: "Shared past" },
    { key: "practice_paper_count", label: "Own practice" },
    { key: "shared_practice_paper_count", label: "Shared practice" },
    { key: "mock_exam_count", label: "Own mocks" },
    { key: "shared_mock_exam_count", label: "Shared mocks" },
  ];

  const visibleSchools = useMemo(() => {
    let list = schools || [];
    if (selectedSchool) {
      list = list.filter((s) => String(s.id) === String(selectedSchool.id));
    } else {
      const q = schoolQuery.trim().toLowerCase();
      if (q) list = list.filter((s) => String(s.name || "").toLowerCase().includes(q));
    }
    const counts = (s) => ({
      papers: (s.papers || []).filter((p) => itemMatchesSubject(p, subjectId)).length,
      mocks: (s.mock_exams || []).filter((m) => itemMatchesSubject(m, subjectId)).length,
    });
    if (missingPapers || noMocks) {
      // Both on: has papers but no mock exam. Counts follow the subject filter.
      list = list.filter((s) => {
        const { papers, mocks } = counts(s);
        if (missingPapers && noMocks) return papers > 0 && mocks === 0;
        return missingPapers ? papers === 0 : mocks === 0;
      });
    } else if (subjectId) {
      list = list.filter((s) => {
        const { papers, mocks } = counts(s);
        return papers > 0 || mocks > 0;
      });
    }
    return list;
  }, [schools, schoolQuery, selectedSchool, subjectId, missingPapers, noMocks]);

  const schoolTableRows = useMemo(
    () =>
      visibleSchools.map((s) => {
        const papers = (s.papers || []).filter((p) => itemMatchesSubject(p, subjectId));
        const mocks = (s.mock_exams || []).filter((m) => itemMatchesSubject(m, subjectId));
        const count = (rows, kind, shared) => rows.filter((row) => (kind ? row.kind === kind : row.kind !== "practice") && Boolean(row.shared) === shared).length;
        return {
          ...s,
          nation: s.nation || "—",
          region: s.region || "—",
          category: s.category || "—",
          paper_count: count(papers, null, false),
          shared_paper_count: count(papers, null, true),
          practice_paper_count: count(papers, "practice", false),
          shared_practice_paper_count: count(papers, "practice", true),
          mock_exam_count: mocks.filter((m) => !m.shared).length,
          shared_mock_exam_count: mocks.filter((m) => m.shared).length,
        };
      })
        // Consortium groups first (then by consortium name), others after; a postcode
        // search keeps nearest-first instead.
        .sort((a, b) =>
          origin
            ? (a.distance_miles ?? 0) - (b.distance_miles ?? 0)
            : (a.consortium_name ? 0 : 1) - (b.consortium_name ? 0 : 1) ||
              String(a.consortium_name || "").localeCompare(String(b.consortium_name || "")) ||
              String(a.name || "").localeCompare(String(b.name || ""))
        ),
    [visibleSchools, subjectId, origin]
  );

  const selectedPapers = useMemo(
    () => (selectedSchool?.papers || []).filter((p) => itemMatchesSubject(p, subjectId)),
    [selectedSchool, subjectId]
  );
  const selectedMocks = useMemo(
    () => (selectedSchool?.mock_exams || []).filter((m) => itemMatchesSubject(m, subjectId)),
    [selectedSchool, subjectId]
  );
  const selectedSubjectChips = useMemo(() => {
    const map = new Map();
    [...(selectedSchool?.papers || []), ...(selectedSchool?.mock_exams || [])].forEach((item) => {
      (item.subjects || []).forEach((s) => {
        if (s?.id && !map.has(Number(s.id))) {
          map.set(Number(s.id), { id: Number(s.id), name: s.name || `Subject ${s.id}` });
        }
      });
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedSchool]);

  const consortiumChoices = useMemo(() => {
    const list = consortiumOptions || [];
    if (!selectionType) return list;
    return list.filter((item) => item.selection_type === selectionType);
  }, [consortiumOptions, selectionType]);

  const regionChartData = useMemo(
    () =>
      (regionSummary || []).map((r) => ({
        region: r.region,
        Grammar: r.grammar,
        Independent: r.independent,
        Total: r.total,
      })),
    [regionSummary]
  );

  return (
    <AnalyticsPageShell
      title="Geographical Insights"
      subtitle="School locations by region, with student and teacher area analytics."
      filters={
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
          <AnalyticsFiltersBar
            courses={courses}
            academicCourses={academicCourses}
            plans={[]}
            courseId={courseId}
            setCourseId={setCourseId}
            showAcademicCourse={false}
            academicCourseId={academicCourseId}
            setAcademicCourseId={setAcademicCourseId}
            showPlan={false}
            showMode
            mode={mode}
            setMode={setMode}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Region</InputLabel>
                <Select
                  label="Region"
                  value={regionId || ""}
                  onChange={(e) => {
                    const nextId = e.target.value || "";
                    setRegionId(nextId);
                    const matched = regions.find((r) => String(r.id) === String(nextId));
                    setNation(matched?.nation || "");
                  }}
                >
                  <MenuItem value="">All regions (UK)</MenuItem>
                  {regions.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.name} ({r.total})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={schools}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                value={selectedSchool}
                inputValue={schoolQuery}
                onInputChange={(e, value, reason) => {
                  setSchoolQuery(value);
                  if (reason === "clear" || reason === "input") {
                    setSelectedSchool(null);
                  }
                }}
                onChange={(e, value) => {
                  setSelectedSchool(value);
                  setSchoolQuery(value?.name || "");
                }}
                renderInput={(params) => (
                  <TextField {...params} label="School name" size="small" placeholder="Search school" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
              <FormControlLabel
                control={<Switch size="small" checked={missingPapers} onChange={(e) => setMissingPapers(e.target.checked)} />}
                label="Missing papers"
              />
              <FormControlLabel
                control={<Switch size="small" checked={noMocks} onChange={(e) => setNoMocks(e.target.checked)} />}
                label="No mock exam"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>School category</InputLabel>
                <Select
                  label="School category"
                  value={category || ""}
                  onChange={(e) => setCategory(e.target.value || "")}
                >
                  <MenuItem value="">All categories</MenuItem>
                  <MenuItem value="grammar">Grammar</MenuItem>
                  <MenuItem value="independent">Independent</MenuItem>
                  <MenuItem value="both">Both</MenuItem>
                  <MenuItem value="other">Other / uncategorised</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Selection type</InputLabel>
                <Select
                  label="Selection type"
                  value={selectionType || ""}
                  onChange={(e) => {
                    const next = e.target.value || "";
                    setSelectionType(next);
                    if (next && consortiumName) {
                      const stillRelevant = consortiumOptions.some(
                        (item) => item.name === consortiumName && item.selection_type === next
                      );
                      if (!stillRelevant) setConsortiumName("");
                    }
                  }}
                >
                  <MenuItem value="">All selection types</MenuItem>
                  {SELECTION_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Consortium</InputLabel>
                <Select
                  label="Consortium"
                  value={consortiumName || ""}
                  onChange={(e) => setConsortiumName(e.target.value || "")}
                >
                  <MenuItem value="">All consortiums</MenuItem>
                  {consortiumChoices.map((item) => (
                    <MenuItem key={`${item.selection_type}-${item.name}`} value={item.name}>
                      {item.name} ({item.school_count})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Exam board</InputLabel>
                <Select label="Exam board" value={examBoard} onChange={(e) => setExamBoard(e.target.value || "")}>
                  <MenuItem value="">All exam boards</MenuItem>
                  {examBoardOptions.map((b) => (
                    <MenuItem key={b.code} value={b.code}>{b.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Gender</InputLabel>
                <Select label="Gender" value={gender} onChange={(e) => setGender(e.target.value || "")}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="boys">Boys</MenuItem>
                  <MenuItem value="girls">Girls</MenuItem>
                  <MenuItem value="co-ed">Co-ed</MenuItem>
                  <MenuItem value="unknown">Not recorded</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ display: "flex", gap: 1 }}>
              <TextField
                size="small"
                label="Postcode"
                placeholder="e.g. ME14 1BQ"
                value={postcodeInput}
                onChange={(e) => setPostcodeInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") setPostcode(postcodeInput.trim());
                }}
                onBlur={() => setPostcode(postcodeInput.trim())}
                sx={{ flex: 1 }}
              />
              <FormControl size="small" sx={{ width: 110 }}>
                <InputLabel>Within</InputLabel>
                <Select label="Within" value={radius} onChange={(e) => setRadius(e.target.value)}>
                  {["5", "10", "25", "50"].map((m) => (
                    <MenuItem key={m} value={m}>{m} miles</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Subject</InputLabel>
                <Select
                  label="Subject"
                  value={subjectId || ""}
                  onChange={(e) => setSubjectId(e.target.value || "")}
                >
                  <MenuItem value="">All subjects</MenuItem>
                  {(subjectOptions ?? []).map((s) => (
                    <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {regions.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {["England", "Northern Ireland", "Scotland", "Wales"].map((nationName) => {
                const group = regions.filter((r) => (r.nation || "") === nationName && (Number(r.total) > 0 || String(regionId) === String(r.id)));
                if (!group.length) return null;
                const grammarTotal = group.reduce((sum, r) => sum + (Number(r.grammar) || 0), 0);
                const independentTotal = group.reduce((sum, r) => sum + (Number(r.independent) || 0), 0);
                const nationSelected = nation === nationName && !regionId;
                const hasMany = group.length > 2;
                const expanded = hasMany ? Boolean(openNations[nationName]) : true;
                return (
                  <Box key={nationName}>
                    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", minWidth: 108 }}>{nationName}</Typography>
                      <Chip
                        size="small"
                        label={`${grammarTotal}G · ${independentTotal}I`}
                        color={nationSelected ? "primary" : "default"}
                        variant={nationSelected ? "filled" : "outlined"}
                        onClick={() => {
                          setNation(nationName);
                          setRegionId("");
                          if (hasMany) setOpenNations((prev) => ({ ...prev, [nationName]: true }));
                        }}
                        sx={{ fontWeight: 700, height: 22, fontSize: 11 }}
                      />
                      {hasMany && (
                        <Chip
                          size="small"
                          label={expanded ? `Hide ${group.length} regions` : `Show ${group.length} regions`}
                          variant="outlined"
                          onClick={() => setOpenNations((prev) => ({ ...prev, [nationName]: !prev[nationName] }))}
                          sx={{ height: 22, fontSize: 11 }}
                        />
                      )}
                    </Box>
                    {expanded && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5, maxHeight: hasMany ? 88 : "none", overflow: hasMany ? "auto" : "visible" }}>
                        {group.map((r) => (
                          <Chip
                            key={r.id}
                            size="small"
                            label={`${r.name} ${r.grammar}G/${r.independent}I`}
                            color={String(regionId) === String(r.id) ? "primary" : "default"}
                            variant={String(regionId) === String(r.id) ? "filled" : "outlined"}
                            onClick={() => {
                              setRegionId(String(r.id));
                              setNation(nationName);
                            }}
                            sx={{ fontWeight: 600, height: 22, fontSize: 11 }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })}
              <Chip
                size="small"
                label="All UK"
                color={!regionId && !nation ? "primary" : "default"}
                variant={!regionId && !nation ? "filled" : "outlined"}
                onClick={() => {
                  setRegionId("");
                  setNation("");
                }}
                sx={{ fontWeight: 600, alignSelf: "flex-start", height: 22, fontSize: 11 }}
              />
            </Box>
          )}
        </Box>
      }
    >
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {TILE_CONFIGS.map(({ key, label, Icon, iconBg, iconColor }) => (
              <Grid item xs={12} sm={6} md={4} lg key={key} sx={{ flexGrow: 1, minWidth: { lg: "18%" } }}>
                <KpiTile label={label} value={summary[key]} Icon={Icon} iconBg={iconBg} iconColor={iconColor} />
              </Grid>
            ))}
          </Grid>

          {Object.keys(dataGaps).length > 0 && (
            <Paper sx={{ ...CARD_SX, mb: 2, border: "1px solid #F5D9A8" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 2, flexWrap: "wrap", mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Data gaps{regionId || nation ? " in this area" : ""}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Same checks as <code>php artisan schools:audit</code>. Fix in Master Forms → Schools.
                </Typography>
              </Box>
              <Grid container spacing={1.5}>
                {Object.entries(dataGaps).map(([key, gap]) => (
                  <Grid item xs={6} sm={4} md={2} key={key}>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setGapKey(gapKey === key ? "" : key)}
                      disabled={!gap.count}
                      sx={{
                        width: "100%",
                        textAlign: "left",
                        p: 1.25,
                        borderRadius: 2,
                        border: gapKey === key ? "2px solid #B4690E" : "1px solid #F5D9A8",
                        bgcolor: gap.count ? "#FFF6E5" : "#F4F6FB",
                        cursor: gap.count ? "pointer" : "default",
                        font: "inherit",
                      }}
                    >
                      <Typography sx={{ fontSize: 22, fontWeight: 800 }}>{gap.count}</Typography>
                      <Typography variant="body2" color="text.secondary">{gap.label}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
              {gapKey && dataGaps[gapKey] && (
                <Box sx={{ mt: 1.5 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {dataGaps[gapKey].label} — {dataGaps[gapKey].count} school{dataGaps[gapKey].count === 1 ? "" : "s"}
                      {dataGaps[gapKey].count > dataGaps[gapKey].schools.length ? ` (first ${dataGaps[gapKey].schools.length})` : ""}
                    </Typography>
                    <Button size="small" component={RouterLink} to="/admin/master-forms" sx={{ textTransform: "none" }}>
                      Open Master Forms
                    </Button>
                  </Box>
                  <Box sx={{ maxHeight: 220, overflow: "auto", display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 0.5 }}>
                    {dataGaps[gapKey].schools.map((s) => (
                      <Typography key={s.id} variant="body2" sx={{ py: 0.25 }}>
                        {s.name} <Box component="span" sx={{ color: "text.secondary" }}>{s.region ? `· ${s.region}` : ""}</Box>
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>
          )}

          <GeographicMap mapConfig={mapConfig} rows={rows} schools={visibleSchools} origin={origin} />

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={CARD_SX}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>Schools by Region</Typography>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={regionChartData} margin={{ top: 0, right: 8, left: -16, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="region" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Grammar" fill="#2563eb" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="Independent" fill="#16a34a" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={CARD_SX}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>Student Numbers by Area</Typography>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={rows} margin={{ top: 0, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="location" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="student_count" name="Students" fill="#7c3aed" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Schools on Map ({visibleSchools.length})
          </Typography>
          {selectedSchool && (
            <Paper sx={{ ...CARD_SX, mb: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{selectedSchool.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[
                      selectedSchool.nation,
                      selectedSchool.region,
                      selectedSchool.category,
                      selectionTypeLabel(selectedSchool.selection_type),
                      selectedSchool.consortium_name,
                    ].filter(Boolean).join(" · ")}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    size="small"
                    variant="contained"
                    component={RouterLink}
                    to={`/admin/papers?school_id=${selectedSchool.id}${subjectId ? `&subject_id=${subjectId}` : ""}`}
                  >
                    Open papers
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    component={RouterLink}
                    to={`/admin/mock-exams?school_id=${selectedSchool.id}${subjectId ? `&subject_id=${subjectId}` : ""}`}
                  >
                    Open mock exams
                  </Button>
                  <Button size="small" onClick={() => { setSelectedSchool(null); setSchoolQuery(""); }}>
                    Clear
                  </Button>
                </Box>
              </Box>
              <SchoolExamProfile profile={selectedSchool.profile} />
              {selectedSubjectChips.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1.5 }}>
                  <Chip
                    size="small"
                    label="All subjects"
                    color={!subjectId ? "primary" : "default"}
                    variant={!subjectId ? "filled" : "outlined"}
                    onClick={() => setSubjectId("")}
                  />
                  {selectedSubjectChips.map((s) => (
                    <Chip
                      key={s.id}
                      size="small"
                      label={s.name}
                      color={String(subjectId) === String(s.id) ? "primary" : "default"}
                      variant={String(subjectId) === String(s.id) ? "filled" : "outlined"}
                      onClick={() => setSubjectId(String(s.id))}
                    />
                  ))}
                </Box>
              )}
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Practice papers{subjectId ? ` (${selectedPapers.length})` : ""}
                  </Typography>
                  {selectedPapers.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No papers linked to this school{subjectId ? " for this subject" : ""}.</Typography>
                  ) : (
                    <List dense>
                      {selectedPapers.map((p) => (
                        <ListItem key={p.id} disableGutters>
                          <ListItemText primary={p.name} secondary={itemSubjectLabel(p)} />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Mock exams{subjectId ? ` (${selectedMocks.length})` : ""}
                  </Typography>
                  {selectedMocks.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No mock exams linked to this school{subjectId ? " for this subject" : ""}.</Typography>
                  ) : (
                    <List dense>
                      {selectedMocks.map((m) => (
                        <ListItem key={m.id} disableGutters>
                          <ListItemText primary={m.name} secondary={itemSubjectLabel(m)} />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Grid>
              </Grid>
            </Paper>
          )}
          <Box sx={{ mb: 3 }}>
            <AnalyticsDataTable columns={schoolColumns} rows={schoolTableRows} loading={false} />
          </Box>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Area Summary</Typography>
          <AnalyticsDataTable columns={areaColumns} rows={rows} loading={false} />
        </>
      )}
    </AnalyticsPageShell>
  );
}
